import { Button, Form, notification, Space, Tooltip } from 'antd';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import ProductPricingDetails from './ProductPricingDetails';
import { CrudSettingsContext } from '@app/contexts/CrudSettingsContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { notificationController } from '@app/controllers/notificationController';
import {
  createProductPricing,
  editProductPricing,
  showProductPricing,
  storeProductPricing,
  updateProductPricing,
} from '@app/api/productPricing.api';
import { setOptions } from 'leaflet';
import { ProductPricingDTO } from '@app/models/product_pricing/ProductPricingDTO';
import { DropdownOption } from '@app/models/DropdownOption';
import { ProductPricing } from '@app/models/product_pricing/ProductPricing';
import { getButtonsByStatus } from '@app/components/statusBar/actionButtons';
import { useNavigate } from 'react-router-dom';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import StatusBar from '@app/components/statusBar/statusBar';

type ProductPricingFormProps = {
  mode: 'create' | 'edit' | 'view';
  id: number | null;
  product_category_id: number | null;
  product_id: number | null;
  product_variety_id: number | null;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
  pageTitle: string;
};

type PricingOptionsContextType = {
  pricingOptions: any[];
  // eslint-disable-next-line @typescript-eslint/ban-types
  setPricingOptions: Function;
  selectedPricingStrategy: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setSelectedPricingStrategy: Function;
};

export const PricingOptionsContext = createContext<PricingOptionsContextType>({
  pricingOptions: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setPricingOptions: () => {},
  selectedPricingStrategy: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedPricingStrategy: () => {},
});

export const ProductPricingForm: React.FC<ProductPricingFormProps> = ({
  id,
  mode,
  setCurrentBreadcrumName,
  pageTitle,
  product_category_id,
  product_id,
  product_variety_id,
}) => {
  const [form] = BaseButtonsForm.useForm();
  const [loading, setLoading] = useState(true);
  const [pricingStrategyOptions, setPricingStrategyOptions] = useState<DropdownOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<DropdownOption[]>([]);
  const [productPricing, setProductPricing] = useState<ProductPricing | null>(null);
  const [pricingOptions, setPricingOptions] = useState<any[]>([]);
  const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);
  const [modelTypeOptions, setModelTypeOptions] = useState<DropdownOption[]>([]);
  const [selectedPricingStrategy, setSelectedPricingStrategy] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const navigate = useNavigate();

  const setApiData = (res: ProductPricingDTO) => {
    setStatus(res?.product_pricing?.status ?? 'Inactive');
    setCurrentBreadcrumName(res?.product_pricing?.code ?? 'New');
    setPricingStrategyOptions(res?.pricing_strategy_options);
    setDepartmentOptions(res?.department_options);
    setProductPricing(res?.product_pricing);
    setStatusOptions(res?.status_options);
    setModelTypeOptions(res?.model_type_options);
    setPricingOptions(res?.product_pricing?.options ?? []);
    setSelectedPricingStrategy(res?.product_pricing?.pricing_strategy ?? '');
    const transformedData = {
      pricing_strategy: res?.pricing_strategy_options?.find((e) => e.value == res?.product_pricing?.pricing_strategy)
        ?.label,
      department_id: res?.department_options?.find((e) => e.value == res?.product_pricing?.department_id)?.label,
      status: res?.status_options?.find((e) => e.value == res?.product_pricing?.status)?.label,
      model_type: res?.model_type_options?.find((e) => e.value == res?.product_pricing?.model_type)?.label,
      code: res?.product_pricing?.code,
      unit: res?.product_pricing?.unit,
    };

    if (mode === 'view') {
      form.setFieldsValue(transformedData);
    } else {
      form.setFieldsValue({
        ...res?.product_pricing,
      });
    }
  };

  const fetchData = () => {
    setLoading(true);
    if (id) {
      if (mode === 'view') {
        showProductPricing(id)
          .then((res: any) => {
            setApiData(res);
            console.log('view data', res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to product pricing', description: err.message });
            console.error(err);
          });
      } else if (mode === 'edit') {
        editProductPricing(id)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit product pricing', description: err.message });
            console.error(err);
          });
      }
    } else if (mode === 'create') {
      createProductPricing()
        .then((res: any) => {
          console.log(res);
          setApiData(res);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch product pricings', description: err.message });
          console.error(err);
        });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = (values: any) => {
    console.log(values);

    const submissionData = {
      ...values,
      options: pricingOptions,
      product_category_id,
      product_id,
      product_variety_id,
    };

    if (mode === 'create') {
      storeProductPricing(submissionData)
        .then((res: any) => {
          setLoading(false);
          form.resetFields();
          navigate('/view-product-pricing', { state: { id: res?.data?.id } });
        })
        .catch((err: any) => {
          notificationController.error({ message: err.message });
          setLoading(false);
        });
    } else if (mode === 'edit' && id) {
      updateProductPricing(id, submissionData)
        .then((res: any) => {
          setLoading(false);
          form.resetFields();
          console.log(res);
          navigate('/view-product-pricing', { state: { id: res?.data?.id } });
        })
        .catch((err: any) => {
          notificationController.error({ message: `${err.message}` });
          setLoading(false);
        });
    }
  };

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol span={24}>
          <StatusBar id={String(id)} status={status} />
        </BaseCol>
        <BaseCol span={24}>
          <BaseCard
            id="productPricingForm"
            title={pageTitle}
            padding="1.25rem"
            // style={{ margin: '20px', marginTop: '0px', width: '98%' }}
            extra={
              mode === 'view' && (
                <Tooltip title="Edit">
                  <Button type="ghost" onClick={() => navigate('/edit-product-pricing', { state: { id: id } })}>
                    Edit
                  </Button>
                </Tooltip>
              )
            }
          >
            <Form
              form={form}
              labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
              labelAlign="left"
              labelWrap
              wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
              size="small"
              onFinish={handleSubmit}
            >
              <BaseRow gutter={[16, 16]}>
                <BaseCol span={24}>
                  <PricingOptionsContext.Provider
                    value={{ pricingOptions, setPricingOptions, selectedPricingStrategy, setSelectedPricingStrategy }}
                  >
                    <ProductPricingDetails
                      productPricing={productPricing}
                      modelTypeOptions={modelTypeOptions}
                      pricingStrategyOptions={pricingStrategyOptions}
                      departmentOptions={departmentOptions}
                      statusOptions={statusOptions}
                      mode={mode}
                    />
                  </PricingOptionsContext.Provider>
                </BaseCol>

                {/* <BaseCol span={24}>
            <Space>
              {mode != 'view' && (
                <BaseButton htmlType="submit" type="primary" size="middle">
                  Save
                </BaseButton>
              )}
              <BaseButton htmlType="button" type="primary" size="middle" onClick={onCancel}>
                {mode == 'view' ? 'Back' : 'Cancel'}
              </BaseButton>
            </Space>
          </BaseCol> */}
              </BaseRow>
            </Form>
          </BaseCard>
        </BaseCol>
      </BaseRow>
    </>
  );
};

export default ProductPricingForm;
